import {
  Center,
  Container,
  Heading,
  Link,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { supportEmail, supportMailTo } from '../data/contact';
import { Authenticated } from './AuthGuards';
import Emoji from './Emoji';
import { Fade } from './Fade';
import { PageHeader } from './PageHeader';

export const PrivacyPolicy = () => (
  <VStack alignItems="flex-start" spacing={4}>
    <Heading size="lg">Privacy Policy</Heading>
    <Text>
      LetterDesk is committed to providing quality services to you and this policy outlines our
      ongoing obligations to you in respect of how we manage your Personal Information.
    </Text>
    <Text>
      We have adopted the Australian Privacy Principles (APPs) contained in the Privacy Act 1988
      (Cth) (the Privacy Act). The NPPs govern the way in which we collect, use, disclose, store,
      secure and dispose of your Personal Information.
    </Text>
    <Text>
      For more information, you can visit the website of The Office of the Australian Information
      Commissioner at <a href="www.aoic.gov.au">www.aoic.gov.au</a> to obtain a copy of the
      Australian Privacy Principles.
    </Text>

    <br />
    <Heading size="md" fontWeight="medium">
      What is Personal Information and why do we collect it?
    </Heading>
    <Text>
      Personal Information is information or an opinion that identifies an individual. Examples of
      Personal Information we collect include: names, addresses, email addresses, phone and
      facsimile numbers.
    </Text>
    <Text>
      This Personal Information is obtained in many ways including correspondence, by email, via our
      website www.letterdesk.app, from media and publications, cookies and other publicly available
      sources.
    </Text>
    <Text>
      We collect your Personal Information for the primary purpose of providing our services to you
      and marketing. We may also use your Personal Information for secondary purposes closely
      related to the primary purpose, in circumstances where you would reasonably expect such use or
      disclosure. You may unsubscribe from our mailing/marketing lists at any time by contacting us
      in writing.
    </Text>
    <Text>
      When we collect Personal Information we will, where appropriate and where possible, explain to
      you why we are collecting the information and how we plan to use it.
    </Text>

    <br />
    <Heading size="md" fontWeight="medium">
      Sensitive Information
    </Heading>
    <Text>
      Sensitive information is defined in the Privacy Act to include information or opinion about
      such things as an individual{"'"}s racial or ethnic origin, political opinions, membership of
      a political association, religious or philosophical beliefs, membership of a trade union or
      other professional body, criminal record or health information.
    </Text>
    <Text>Sensitive information will be used by us only:</Text>
    <UnorderedList pl={4}>
      <ListItem>For the primary purpose for which it was obtained</ListItem>
      <ListItem>For a secondary purpose that is directly related to the primary purpose</ListItem>
      <ListItem>With your consent; or where required or authorised by law.</ListItem>
    </UnorderedList>

    <br />
    <Heading size="md" fontWeight="medium">
      Disclosure of Personal Information
    </Heading>
    <Text>
      Your Personal Information may be disclosed in a number of circumstances including the
      following:
    </Text>
    <UnorderedList pl={4}>
      <ListItem>Third parties where you consent to the use or disclosure; and</ListItem>
      <ListItem>Where required or authorised by law.</ListItem>
    </UnorderedList>

    <br />
    <Heading size="md" fontWeight="medium">
      Security of Personal Information
    </Heading>
    <Text>
      Your Personal Information is stored in a manner that reasonably protects it from misuse and
      loss and from unauthorized access, modification or disclosure.
    </Text>

    <br />
    <Heading size="md" fontWeight="medium">
      Maintaining the Quality of your Personal Information
    </Heading>
    <Text>
      It is an important to us that your Personal Information is up to date. We will take reasonable
      steps to make sure that your Personal Information is accurate, complete and up-to-date. If you
      find that the information we have is not up to date or is inaccurate, please advise us as soon
      as practicable so we can update our records and ensure we can continue to provide quality
      services to you.
    </Text>

    <br />
    <Heading size="md" fontWeight="medium">
      Policy Updates
    </Heading>
    <Text>This Policy may change from time to time.</Text>
    <br />
    <Heading size="md" fontWeight="medium">
      Privacy Policy Complaints and Enquiries
    </Heading>
    <Text>
      For any questions or concerns regarding our Privacy Policy pelase contact us at:{' '}
      <a href={supportMailTo}>{supportEmail}</a>
    </Text>
  </VStack>
);

const Issues = () => (
  <VStack alignItems="flex-start">
    <Heading size="md" fontWeight="medium">
      <Emoji symbol="🐛" label="Emoji of a catepillar" /> I{"'"}ve encountered an issue:
    </Heading>
    <Text>
      Please send me an email at:{' '}
      <Link textDecoration={'underline'} href={supportMailTo}>
        {supportEmail}
      </Link>{' '}
      containing:
    </Text>
    <OrderedList pl={4}>
      <ListItem>A description of the problem</ListItem>
      <ListItem>Steps to reproduce the issue</ListItem>
      <ListItem>Relevant screenshots and information where applicable</ListItem>
    </OrderedList>
    <Text>
      Please note that LetterDesk is being built by one human{' '}
      <a href="https://twitter.com/ZaymonTheUnwise">(@ZaymonTheUnwise)</a>. We&apos;re not a large
      company and might not be able to get back to you right away. However, we really value you
      taking the time to contact us <Emoji symbol="😌" label="Emoji of a content happy face" />.
    </Text>
  </VStack>
);

const Ideas = () => (
  <VStack alignItems="flex-start">
    <Heading size="md" fontWeight="medium">
      <Emoji symbol="💡" label="Emoji of a lightbulb" /> I have an idea:
    </Heading>
    <Text>
      Awesome! Please send us an email at:{' '}
      <Link textDecoration={'underline'} href={supportMailTo}>
        {supportEmail}
      </Link>{' '}
      and tell us all about it. We&apos;re always looking to improve and evolve the platform and
      I&apos;d love to hear from you.
    </Text>
  </VStack>
);

export const HelpAndFeedback = () => (
  <Authenticated>
    <Fade>
      <Container w={['xs', 'md', 'lg']} pb={16}>
        <Center>
          <VStack spacing={8} align="flex-start">
            <PageHeader
              title="Help and Feedback"
              emoji={{ symbol: '🙋‍♀️', label: 'Emoji of a woman with raised hand' }}
            />

            <Issues />
            <Ideas />
            <PrivacyPolicy />
          </VStack>
        </Center>
      </Container>
    </Fade>
  </Authenticated>
);
